// ─────────────────────────────────────────
// GroupChat.js — full Discord-style chat thread for a group
// ─────────────────────────────────────────
// Used in both ChatPage (main chat hub) and GroupPage (in-group chat tab).
// Props: { group, session, myProfile, isMember, markSeen? }

import { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import AvatarImg from './AvatarImg'

export default function GroupChat({ group, session, myProfile, isMember, markSeen, fullHeight }) {
  const [messages, setMessages] = useState([])
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [attachFile, setAttachFile] = useState(null)
  const [attachPreview, setAttachPreview] = useState(null)
  const [attachIsVideo, setAttachIsVideo] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState(null)
  const [sendError, setSendError] = useState(null)
  const bottomRef = useRef(null)
  const inputRef = useRef(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    fetchMessages()

    const channel = supabase
      .channel(`group-chat-${group.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'group_messages',
        filter: `group_id=eq.${group.id}`
      }, async payload => {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, username, avatar_emoji, avatar_url')
          .eq('id', payload.new.user_id)
          .single()
        const msg = { ...payload.new, profiles: profile || null }
        setMessages(prev => {
          if (prev.some(m => m.id === payload.new.id)) return prev
          // Replace a matching local message from the same user sent within 30s
          const localIdx = prev.findIndex(m =>
            String(m.id).startsWith('local-') &&
            m.user_id === payload.new.user_id &&
            m.content === payload.new.content
          )
          if (localIdx !== -1) {
            const next = [...prev]
            next[localIdx] = msg
            return next
          }
          return [...prev, msg]
        })
        if (markSeen) markSeen(group.id, payload.new.id)
      })
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [group.id])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function fetchMessages() {
    setLoading(true)
    const { data: msgRows } = await supabase
      .from('group_messages')
      .select('id, group_id, user_id, content, image_url, video_url, created_at')
      .eq('group_id', group.id)
      .order('created_at', { ascending: true })
      .limit(200)

    const rows = msgRows || []
    if (rows.length > 0) {
      const userIds = [...new Set(rows.map(m => m.user_id))]
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id, username, avatar_emoji, avatar_url')
        .in('id', userIds)
      const profileMap = {}
      ;(profileData || []).forEach(p => { profileMap[p.id] = p })
      const enriched = rows.map(m => ({ ...m, profiles: profileMap[m.user_id] || null }))
      setMessages(enriched)
      if (markSeen) markSeen(group.id, rows[rows.length - 1].id)
    } else {
      setMessages([])
    }
    setLoading(false)
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'instant' }), 50)
  }

  function handleAttachSelect(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 100 * 1024 * 1024) { alert('File must be under 100MB'); return }
    setAttachFile(file)
    setAttachIsVideo(file.type.startsWith('video/'))
    setAttachPreview(URL.createObjectURL(file))
  }

  function clearAttach() {
    setAttachFile(null)
    setAttachPreview(null)
    setAttachIsVideo(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function handleSend() {
    const content = text.trim()
    if ((!content && !attachFile) || sending) return
    setSending(true)
    setText('')

    let image_url = null
    let video_url = null
    if (attachFile) {
      setUploading(true)
      const ext = attachFile.name.split('.').pop()
      const path = `${group.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('chat-images')
        .upload(path, attachFile, { contentType: attachFile.type })
      if (!uploadError && uploadData) {
        const { data: urlData } = supabase.storage.from('chat-images').getPublicUrl(path)
        const url = urlData?.publicUrl || null
        if (attachIsVideo) video_url = url
        else image_url = url
      }
      setUploading(false)
    }

    const { error } = await supabase.from('group_messages').insert({
      group_id: group.id,
      user_id: session.user.id,
      content: content || null,
      image_url,
      video_url
    })

    if (error) {
      setText(content)
      setSendError(error.message)
      setTimeout(() => setSendError(null), 6000)
    } else {
      setSendError(null)
      // Append directly to state — don't re-fetch (avoids RLS SELECT issues)
      const newMsg = {
        id: `local-${Date.now()}`,
        group_id: group.id,
        user_id: session.user.id,
        content: content || null,
        image_url,
        video_url,
        created_at: new Date().toISOString(),
        profiles: myProfile || null
      }
      setMessages(prev => [...prev, newMsg])
    }

    clearAttach()
    setSending(false)
    inputRef.current?.focus()
  }

  // Build flat list: date dividers + message bursts
  function buildItems(msgs) {
    const out = []
    let lastDay = null
    let burst = null
    msgs.forEach((msg, i) => {
      const day = new Date(msg.created_at).toDateString()
      const prev = msgs[i - 1]
      const sameSender = prev && prev.user_id === msg.user_id
      const closeInTime = prev && (new Date(msg.created_at) - new Date(prev.created_at)) < 5 * 60 * 1000
      const sameDay = prev && new Date(prev.created_at).toDateString() === day

      if (day !== lastDay) {
        out.push({ type: 'divider', label: dateDividerLabel(msg.created_at) })
        lastDay = day
        burst = null
      }

      if (burst && sameSender && closeInTime && sameDay) {
        burst.messages.push(msg)
      } else {
        burst = { type: 'burst', user_id: msg.user_id, profile: msg.profiles, messages: [msg] }
        out.push(burst)
      }
    })
    return out
  }

  function timeLabel(dateStr) {
    const d = new Date(dateStr)
    const now = new Date()
    const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1)
    const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    if (d.toDateString() === now.toDateString()) return `Today at ${timeStr}`
    if (d.toDateString() === yesterday.toDateString()) return `Yesterday at ${timeStr}`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ` at ${timeStr}`
  }

  function dateDividerLabel(dateStr) {
    const d = new Date(dateStr)
    const now = new Date()
    const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1)
    if (d.toDateString() === now.toDateString()) return 'Today'
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
  }

  const isMe = uid => uid === session.user.id
  const items = buildItems(messages)

  if (loading) return <div style={s.loading}>Loading...</div>

  const wrapperStyle = fullHeight
    ? { ...s.wrapper, height: '100%', minHeight: 0 }
    : s.wrapper

  return (
    <div style={wrapperStyle}>

      {/* Lightbox */}
      {lightboxUrl && (
        <div style={s.lightbox} onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} style={s.lightboxImg} alt="full size" onClick={e => e.stopPropagation()} />
          <button style={s.lightboxClose} onClick={() => setLightboxUrl(null)}>✕</button>
        </div>
      )}

      {/* Channel header */}
      <div style={s.channelHeader}>
        <div style={s.channelIcon}>{group.icon_emoji || '⛳'}</div>
        <div style={s.channelHeaderInfo}>
          <div style={s.channelName}>{group.name}</div>
          {group.description && <div style={s.channelDesc}>{group.description}</div>}
        </div>
      </div>

      {/* Messages area */}
      <div style={s.messageArea}>
        {messages.length === 0 && (
          <div style={s.emptyChat}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>{group.icon_emoji || '⛳'}</div>
            <div style={s.emptyChatTitle}>Welcome to #{group.name}</div>
            <div style={s.emptyChatSub}>This is the beginning of the group chat. Say something!</div>
          </div>
        )}

        {items.map((item, idx) => {
          if (item.type === 'divider') {
            return (
              <div key={`d-${idx}`} style={s.dateDivider}>
                <div style={s.dateDividerLine} />
                <div style={s.dateDividerLabel}>{item.label}</div>
                <div style={s.dateDividerLine} />
              </div>
            )
          }

          const mine = isMe(item.user_id)
          const profile = item.profile
          const firstMsg = item.messages[0]
          const profileLink = mine ? '/passport' : `/passport/${item.user_id}`

          return (
            <div key={`b-${idx}`} style={s.burst}>
              <div style={s.burstHeader}>
                <Link to={profileLink} style={s.burstAvatarLink}>
                  <div style={{
                    ...s.burstAvatar,
                    background: mine
                      ? 'linear-gradient(135deg,#0e1f13,#1a5e30)'
                      : 'linear-gradient(135deg,#2aad56,#1a7a3c)'
                  }}>
                    <AvatarImg profile={profile} />
                  </div>
                </Link>
                <div style={s.burstMeta}>
                  <Link to={profileLink} style={{ ...s.burstUsername, color: mine ? '#0e1f13' : '#1a7a3c', textDecoration: 'none' }}>
                    @{profile?.username || 'member'}
                    {mine && <span style={s.youTag}> you</span>}
                  </Link>
                  <span style={s.burstTime}>{timeLabel(firstMsg.created_at)}</span>
                </div>
              </div>

              <div style={s.burstMessages}>
                {item.messages.map(msg => (
                  <div key={msg.id}>
                    {msg.content && <div style={s.messageText}>{msg.content}</div>}
                    {msg.image_url && (
                      <div style={s.mediaWrap}>
                        <img
                          src={msg.image_url}
                          style={s.messageImage}
                          alt="attachment"
                          onClick={() => setLightboxUrl(msg.image_url)}
                          loading="lazy"
                        />
                      </div>
                    )}
                    {msg.video_url && (
                      <div style={s.mediaWrap}>
                        <video src={msg.video_url} style={s.messageVideo} controls playsInline preload="metadata" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}

        <div ref={bottomRef} />
      </div>

      {/* Attachment preview */}
      {attachPreview && (
        <div style={s.attachPreviewBar}>
          {attachIsVideo
            ? <video src={attachPreview} style={s.attachThumb} playsInline muted />
            : <img src={attachPreview} style={s.attachThumb} alt="preview" />
          }
          <div style={s.attachInfo}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#0e1f13' }}>{attachFile?.name}</div>
            <div style={{ fontSize: 11, color: '#6b7c6e' }}>{attachFile ? `${(attachFile.size / 1024).toFixed(0)} KB` : ''}</div>
          </div>
          <button style={s.attachRemove} onClick={clearAttach}>✕</button>
        </div>
      )}

      {/* Send error */}
      {sendError && (
        <div style={s.sendErrorBar}>⚠ {sendError}</div>
      )}

      {/* Input bar */}
      {isMember ? (
        <div style={s.inputBar}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            style={{ display: 'none' }}
            onChange={handleAttachSelect}
          />
          <button style={s.attachBtn} onClick={() => fileInputRef.current?.click()} disabled={sending} title="Attach image or video">
            📎
          </button>
          <input
            ref={inputRef}
            style={s.input}
            placeholder={`Message ${group.name}...`}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            maxLength={2000}
            disabled={sending}
          />
          <button
            style={{ ...s.sendBtn, opacity: (text.trim() || attachFile) && !sending && !uploading ? 1 : 0.35 }}
            onClick={handleSend}
            disabled={(!text.trim() && !attachFile) || sending || uploading}
          >
            {uploading ? '⏳' : '↑'}
          </button>
        </div>
      ) : (
        <div style={s.notMemberBar}>Join the group to participate in chat</div>
      )}
    </div>
  )
}

const s = {
  wrapper: {
    display: 'flex', flexDirection: 'column',
    height: 'calc(100vh - 220px)',
    minHeight: 400,
    overflow: 'hidden',
    background: '#f4f7f5', fontFamily: "'Barlow', sans-serif"
  },
  loading: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#6b7c6e', fontSize: 14
  },

  // Lightbox
  lightbox: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 999, cursor: 'zoom-out'
  },
  lightboxImg: { maxWidth: '92vw', maxHeight: '88vh', borderRadius: 8, objectFit: 'contain' },
  lightboxClose: {
    position: 'absolute', top: 18, right: 18,
    background: 'rgba(255,255,255,0.15)', border: 'none',
    color: 'white', fontSize: 18, width: 36, height: 36,
    borderRadius: '50%', cursor: 'pointer'
  },

  // Channel header
  channelHeader: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '14px 20px', background: 'white',
    borderBottom: '1px solid #e8ede9', flexShrink: 0
  },
  channelIcon: {
    fontSize: 22, width: 38, height: 38, borderRadius: 10,
    background: 'linear-gradient(135deg,#0e1f13,#1a5e30)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  channelHeaderInfo: { flex: 1 },
  channelName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 17, color: '#0e1f13', letterSpacing: 0.3
  },
  channelDesc: { fontSize: 11, color: '#9aaa9d', marginTop: 1 },

  // Messages
  messageArea: {
    flex: 1, overflowY: 'auto',
    padding: '12px 0 8px',
    display: 'flex', flexDirection: 'column'
  },
  emptyChat: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '60px 32px', textAlign: 'center'
  },
  emptyChatTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 20, color: '#0e1f13', marginBottom: 8
  },
  emptyChatSub: { fontSize: 13, color: '#6b7c6e', lineHeight: 1.5 },

  // Date divider
  dateDivider: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 20px', marginTop: 4
  },
  dateDividerLine: { flex: 1, height: 1, background: '#e8ede9' },
  dateDividerLabel: {
    fontSize: 10, fontWeight: 700, color: '#9aaa9d',
    letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap'
  },

  // Burst (Discord style)
  burst: { padding: '4px 20px 2px', display: 'flex', flexDirection: 'column', gap: 2 },
  burstHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 },
  burstAvatarLink: { textDecoration: 'none', flexShrink: 0 },
  burstAvatar: {
    width: 36, height: 36, borderRadius: '50%', fontSize: 18, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
  },
  burstMeta: { display: 'flex', alignItems: 'baseline', gap: 8 },
  burstUsername: { fontWeight: 800, fontSize: 14 },
  youTag: { fontSize: 10, color: '#9aaa9d', fontWeight: 400, fontStyle: 'italic' },
  burstTime: { fontSize: 11, color: '#b0b8b2' },
  burstMessages: {
    paddingLeft: 48,
    display: 'flex', flexDirection: 'column', gap: 4
  },
  messageText: {
    fontSize: 14, color: '#0e1f13', lineHeight: 1.5,
    wordBreak: 'break-word', whiteSpace: 'pre-wrap'
  },
  mediaWrap: { marginTop: 4 },
  messageImage: {
    maxWidth: 360, maxHeight: 280, borderRadius: 10,
    cursor: 'zoom-in', display: 'block',
    border: '1px solid #e8ede9', objectFit: 'cover'
  },
  messageVideo: {
    maxWidth: 360, maxHeight: 300, borderRadius: 10,
    display: 'block', border: '1px solid #e8ede9', background: '#000'
  },

  // Attachment preview
  attachPreviewBar: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 16px', background: '#e8f5ed', borderTop: '1px solid #c8e6d0'
  },
  attachThumb: { width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: '1px solid #c8e6d0' },
  attachInfo: { flex: 1 },
  attachRemove: { background: 'none', border: 'none', color: '#c0392b', fontSize: 14, cursor: 'pointer', padding: '4px 6px' },

  // Input bar
  inputBar: {
    display: 'flex', gap: 8, alignItems: 'center',
    padding: '10px 16px 14px', background: 'white',
    borderTop: '1px solid #e8ede9', flexShrink: 0
  },
  attachBtn: {
    background: 'none', border: 'none', fontSize: 20,
    cursor: 'pointer', padding: '4px 6px', flexShrink: 0,
    color: '#6b7c6e', borderRadius: 6
  },
  input: {
    flex: 1, background: '#f4f7f5', border: '1.5px solid #e2ebe4',
    borderRadius: 22, padding: '10px 16px', fontSize: 14,
    color: '#0e1f13', outline: 'none', fontFamily: "'Barlow', sans-serif"
  },
  sendBtn: {
    width: 40, height: 40, borderRadius: '50%',
    background: '#0e1f13', color: '#c8f135', border: 'none',
    fontSize: 18, fontWeight: 900, cursor: 'pointer', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  sendErrorBar: {
    padding: '8px 16px', background: 'rgba(192,57,43,0.1)',
    borderTop: '1px solid rgba(192,57,43,0.3)',
    fontSize: 12, color: '#c0392b', fontWeight: 600, flexShrink: 0
  },
  notMemberBar: {
    padding: '16px', textAlign: 'center', fontSize: 13,
    color: '#6b7c6e', background: 'white', borderTop: '1px solid #e8ede9', fontStyle: 'italic'
  }
}
